/**
 * Display-time sanity gate (MER-441).
 *
 * A DWS live sweep before the client meeting found impossible figures on the
 * dashboard: $46,454 / ton, $50,069 / mile, a -3500% route CM, a 99.4% customer
 * margin. These are the symptom of a near-zero denominator (tons / miles / cost
 * coverage) the dashboard can't re-derive without a golden. Rather than fake the
 * denominators, gate the OUTPUT: a value outside a sane range renders the
 * existing "Pending / insufficient source data" state instead of the number.
 * An honest pending beats an impossible figure in front of a client. Same spirit
 * as the labor-coverage suppression (MER-426); belt to the pipeline-side check
 * (MER-397).
 *
 * Pure (no React) so the rule is unit-testable and shared across the KPI tile,
 * comparison panes, the measure pivot, and the chart point filter.
 */

import type { KpiFormat } from "./format";

export interface DisplayBounds {
  min?: number;
  max?: number;
}

/**
 * Customer-facing copy for a gated value (MER-446). "Not available" is honest
 * about the absence; "awaiting source data" tells the client it is a feed gap,
 * not a broken dashboard. Never a number when the denominator is starved.
 */
export const GATE_COPY = "Not available - awaiting source data";

/**
 * MER-819: the same shape of blank, with the true cause.
 *
 * A value can be missing for two reasons that look identical on screen and
 * are opposite in what they ask of the reader. GATE_COPY is a FEED gap: the
 * data has not arrived. This is a DEFINITION gap: the data is here and we
 * have never asked this hauler which of it counts. Rendering the second as
 * the first sends an owner to check an upload that is fine, and quietly
 * hides the fact that the open question is ours.
 *
 * Kept byte-identical to customer_ontology.AWAITING_COPY on the pipeline
 * side; tests/unit/test_customer_ontology.py reads this file and asserts the
 * two strings match, so the two halves of one sentence cannot drift apart.
 */
export const DEFINITION_GATE_COPY = "Not available - awaiting definition";

/**
 * MER-446 / MER-483: classify a query error as "the manifest references
 * a column or table the pipeline does not emit" (Postgres 42703 /
 * PostgREST schema-cache miss). These render the honest GATE_COPY
 * state; raw SQL errors must never reach a client page. Shared by
 * WidgetRenderer (widget-level fetch) and ChartWidget (per-series
 * fetch), which previously bypassed the gate and rendered the raw
 * message (the live arpc red box on Exec Roll-Up, MER-483 finding 1).
 */
const COLUMN_MISSING_RE =
  /does not exist|could not find|42703|PGRST204|PGRST205|MERIDIAN_NO_SOURCE_DATA/i;

export function isColumnMissingError(message: string | null | undefined): boolean {
  if (!message) return false;
  return COLUMN_MISSING_RE.test(message);
}

/**
 * MER-589 half B: marker thrown by `resolveLatestFilters` when a widget pins a
 * column to its latest value but the source carries no rows at all for this
 * tenant. Best Disposal has no Samsara feed, so its eight Safety tiles have no
 * asset rows; before this they rendered 0, which reads as perfect safety rather
 * than "no data". Routed through `isColumnMissingError` so those tiles render
 * GATE_COPY, the same honest state a phantom column produces.
 *
 * Deliberately NOT used when rows exist but the pinned column is entirely null.
 * That is a pipeline defect, not a feed gap, and it must surface as an error
 * rather than be dressed up as an absent feed.
 */
export const NO_SOURCE_DATA = "MERIDIAN_NO_SOURCE_DATA";

/**
 * +/-200% expressed as a fraction. MER-184 stores percent columns as fractions
 * (0.42 = 42%), so 2.0 == 200%. A route CM of -3500% is -35.0 here.
 */
export const PCT_DISPLAY_LIMIT = 2.0;

/**
 * True when `value` is not safe to show a customer: non-finite, outside the
 * widget's `display_bounds`, or a percent beyond +/-200%. Null / non-numeric
 * values are not gated here -- the caller's existing empty/loading states own
 * those.
 */
export function isSanityGated(
  value: number | string | null | undefined,
  format: KpiFormat,
  bounds?: DisplayBounds,
  applyPctLimit: boolean = true,
): boolean {
  if (value === null || value === undefined || typeof value !== "number") return false;
  if (!Number.isFinite(value)) return true;
  if (bounds) {
    if (bounds.min !== undefined && value < bounds.min) return true;
    if (bounds.max !== undefined && value > bounds.max) return true;
  }
  if (applyPctLimit && format === "percent" && Math.abs(value) > PCT_DISPLAY_LIMIT) return true;
  return false;
}

/**
 * True for percent columns whose magnitude is legitimately unbounded -- growth,
 * variance, and period-over-period change can exceed +/-200% for real (a new
 * service line at +300% YoY, a recovery month at +400% MoM, and especially the
 * Data Quality "Period Variance MoM" tile whose entire job is to surface a big
 * swing). The +/-200% display gate would wrongly blank those, so callers pass
 * `applyPctLimit: false` for them. Explicit `display_bounds` still apply.
 */
export function isUnboundedPctColumn(column?: string): boolean {
  if (!column) return false;
  const c = column.toLowerCase();
  return /(growth|variance|_change_pct$|_change$|_yoy$|_mom$|_delta_pct$|_delta$)/.test(c);
}

/**
 * Default sane range for physically-bounded ratio metrics, so the gate works
 * without per-widget config and BD inherits it automatically. $/ton and $/mile
 * (cost / fuel / maintenance) have hard ceilings; a starved denominator blows
 * far past them ($46,454/ton, $50,069/mile, $478 fuel/mile). An explicit
 * `display_bounds` on the widget always wins over this.
 */
/**
 * MER-589 follow-up (Seth B2 / B3): physically-bounded operational metrics.
 *
 * Seth flagged lifts per hour in April 2026 at 63 to 73 against a 7 to 13
 * industry band. It was never gated, and by 2026-09-01 Best Disposal's
 * production values had a MEDIAN of 52.87 and a maximum of 208.05. Delta's
 * maximum is 38.18. The whole distribution is wrong, not just the tail, which
 * is the signature of a starved denominator rather than a busy crew.
 *
 * This table is the class fix he actually asked for: a bound belongs to the
 * COLUMN, so every tenant and every widget inherits it and a new customer is
 * protected on day one. A widget's own `display_bounds` still wins.
 *
 * Each entry must be justifiable from physics or equipment, not from the data.
 * A bound derived from the observed values would move whenever the defect
 * moves, which is how $18/ton survived review for weeks.
 */
const PHYSICAL_BOUNDS: ReadonlyArray<readonly [RegExp, DisplayBounds]> = [
  // A front-load lift cycle is approach, engage, hoist, dump, set down, depart.
  // Two minutes is already a fast crew, so 30 in a sustained hour is a generous
  // ceiling and anything above it is a denominator problem.
  [/^lifts_per_hour$/, { min: 0, max: 30 }],
  // A roll-off haul is drive out, load the can, drive to the facility, dump,
  // return. Best Disposal has rows at 0.02 hours, which is 72 seconds for that
  // whole cycle. The floor is what catches it; the ceiling catches a stalled
  // clock that never closed.
  [/^hours_per_haul$/, { min: 0.1, max: 24 }],
  // stops_per_hour is now computed for every line of business, not just ASL, so
  // it inherits the same starved-denominator exposure lifts_per_hour had. The
  // ceiling is set by the FASTEST stop type there is: an automated side-load
  // cart arm cycle plus curb-to-curb travel is roughly 20 to 30 seconds, so 150
  // is a stop every 24 seconds sustained for a full hour and nothing real
  // exceeds it. It is deliberately loose for front load, where the tighter
  // equipment-specific ceiling lives on lifts_per_hour above.
  [/^stops_per_hour$/, { min: 0, max: 150 }],
  // Front-load containers run 2 to 8 cubic yards. Observed values sit at 6.92
  // to 7.59 on both tenants, so this gates nothing today and catches the
  // starved-denominator case later.
  [/^yards_per_lift$/, { min: 0, max: 12 }],
  // MER-825: swaps are one of the three service types a pull counts, so the
  // numerator is a subset of the denominator and the share cannot exceed 1.
  // That is arithmetic, not an observed range, which is the only kind of
  // bound MER-716 lets us state. A value above 1 means the two halves came
  // off different populations, which is the defect MER-630 found on the haul
  // tile and the one this shape exists to make impossible.
  [/^swap_share_of_pulls$/, { min: 0, max: 1 }],
];

export function defaultBoundsForColumn(column?: string): DisplayBounds | undefined {
  if (!column) return undefined;
  const c = column.toLowerCase();
  for (const [pattern, bounds] of PHYSICAL_BOUNDS) {
    if (pattern.test(c)) return bounds;
  }
  if (/_per_ton$/.test(c)) return { min: 0, max: 1000 };
  if (/_per_mile$/.test(c)) return { min: 0, max: 100 };
  // MER-446: a collection rate is a fraction (1.0 == 100%). The live $4,240
  // (424,000%) is a starved denominator. Cap just above 100% for rounding.
  if (/collection_rate/.test(c)) return { min: 0, max: 1.5 };
  // MER-446: a gross-margin % is a fraction in [-1, ~1). Waste hauling never
  // earns a true 100% margin (there is always cost), and the live +100% /
  // -417% / -7,050% are cost-attribution garbage on tiny-revenue slices. Scoped
  // to the *_pct margin (fraction), never the $-per-stop margin.
  if (/margin_pct$/.test(c)) return { min: -1, max: 0.98 };
  return undefined;
}

/**
 * MER-449: customer-facing copy for a value we KNOW is wrong and are actively
 * fixing -- a calc/binding bug, not a missing feed. Distinct from GATE_COPY
 * ("awaiting source data"): the data is present, the number is just not
 * trustworthy yet. Used for the +/-100% ratio band (impossible % of revenue,
 * impossible MoM variance) and the known-broken tiles in FORCE_REVIEW_COLUMNS.
 */
export const REVIEW_COPY = "Not available, under review";

/**
 * MER-684 / MER-715: copy for a stored ratio the dashboard refuses to combine
 * across rows. yards_per_haul and hours_per_haul are stored per division and
 * period, but the components that would let several rows combine as
 * SUM(numerator) / SUM(denominator) are not stored, and averaging the stored
 * ratios is the defect the ratio contract removes. One division and one month
 * select exactly one stored row, which is an honest reading; anything wider
 * says so instead of printing an average. "Month" rather than "period" because
 * the weekly default window holds several weekly rows even for one division,
 * and the month selector is the control that narrows it to one row.
 */
export const UNCOMBINABLE_COPY = "Not combinable yet - select one division and one month";

/**
 * MER-449: a percent tile (stored as a fraction since MER-184, 1.0 == 100%)
 * reading beyond +/-100% of its own base is business-impossible at egress --
 * fuel cannot be 161% of revenue, a month cannot vary 17,880% from the prior
 * one. Tighter than PCT_DISPLAY_LIMIT (200%) and -- unlike it -- applied even
 * to the growth/variance columns isUnboundedPctColumn() exempts, because the
 * demo bar is "no impossible number on screen," not "let variance run free."
 */
export const PCT_REVIEW_LIMIT = 1.0;

/**
 * MER-449: tiles whose value sits INSIDE the +/-100% band (so the range check
 * can't catch them) yet is still business-impossible because the underlying
 * calc/binding is broken. Rendered as REVIEW_COPY until the real fix ships.
 * Remove a column here the moment its fix lands so the real number shows.
 *   - churn_rate_pct       DWS 86.3% / BD 82.2%  -> real fix MER-323
 *   - billing_accuracy_rate            51.6%     -> real fix MER-313
 */
export const FORCE_REVIEW_COLUMNS = new Set(["churn_rate_pct", "billing_accuracy_rate"]);

/**
 * MER-449: true when a percent / ratio value is beyond the +/-100% display
 * band. Percent values are fractions (1.0 == 100%); ratio values are raw
 * multipliers, where only an absurd >100x signals a starved denominator (a
 * real fee-recovery ratio never exceeds a few x). Other formats are never
 * band-gated here. Non-finite is owned by isSanityGated, so skip it.
 */
export function isOutsideReviewBand(value: number, format: KpiFormat): boolean {
  if (!Number.isFinite(value)) return false;
  if (format === "percent") return Math.abs(value) > PCT_REVIEW_LIMIT;
  if (format === "ratio") return Math.abs(value) > 100;
  return false;
}

export interface GateResult {
  /** Customer-facing copy to render in place of the number. */
  copy: string;
}

/**
 * MER-449 egress safety resolver for single-number tiles. Returns the copy to
 * render instead of the value, or null to render the number. Precedence:
 *   1. Known-broken column        -> REVIEW_COPY (explicit override, always wins)
 *   2. Non-finite / out-of-bounds -> GATE_COPY   (MER-441/446 feed-gap framing)
 *   3. pct/ratio beyond +/-100%   -> REVIEW_COPY (MER-449 range check)
 *
 * Step 2 calls isSanityGated with applyPctLimit=false so the tighter +/-100%
 * band in step 3 owns percent columns (including the unbounded growth/variance
 * ones); the explicit per-ton / per-mile / collection / margin bounds still
 * gate as "awaiting source data," preserving the MER-446 states Cowork verified.
 */
export function resolveTileGate(
  value: number | string | null | undefined,
  format: KpiFormat,
  opts: { column?: string; bounds?: DisplayBounds } = {},
): GateResult | null {
  if (value === null || value === undefined || typeof value !== "number") return null;
  const column = opts.column?.toLowerCase();
  if (column && FORCE_REVIEW_COLUMNS.has(column)) return { copy: REVIEW_COPY };
  if (isSanityGated(value, format, opts.bounds, false)) return { copy: GATE_COPY };
  if (isOutsideReviewBand(value, format)) return { copy: REVIEW_COPY };
  return null;
}
